import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { CapaCreateDialog } from "@/components/quality/CapaCreateDialog";
import {
  ClipboardCheck, Search, CheckCircle2, XCircle, MinusCircle, Circle, AlertTriangle, Image as ImageIcon, ClipboardPlus,
} from "lucide-react";

const cpTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "pass") return { icon: CheckCircle2, cls: "text-emerald-600", label: "Pass" };
  if (s === "fail") return { icon: XCircle, cls: "text-rose-600", label: "Fail" };
  if (s === "na" || s === "n/a") return { icon: MinusCircle, cls: "text-stone-400", label: "N/A" };
  return { icon: Circle, cls: "text-stone-300", label: "Pending" };
};

// Path -> preset checklist section focus.
const PATH_SECTION = {
  "/hygiene": ["hygiene", "cleaning"], "/temperature": ["temperature"],
  "/production-qa": ["quality", "execution"], "/maintenance": ["maintenance"],
  "/fssai-checks": ["fssai"],
};
const TITLES = {
  "/hygiene": "Hygiene / Cleaning", "/temperature": "Temperature Checks",
  "/production-qa": "Production QA", "/maintenance": "Maintenance Checks",
  "/fssai-checks": "FSSAI Checks", "/qa-audits": "QA Audits",
};

export default function QualityAudits() {
  const { pathname } = useLocation();
  const preset = PATH_SECTION[pathname] || null;
  const title = TITLES[pathname] || "QA Audits";

  const { data, isLoading, isError, error, refetch } = useResource("/audits");
  const audits = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [selected, setSelected] = useState(null);
  const [capaOpen, setCapaOpen] = useState(false);
  const [capaPrefill, setCapaPrefill] = useState(null);

  const sevFromPriority = (p) => {
    const v = String(p || "").toLowerCase();
    if (v === "high" || v === "critical") return v === "critical" ? "Critical" : "High";
    if (v === "low") return "Low";
    return "Medium";
  };
  const raiseCapa = (section, cp) => {
    setCapaPrefill({
      type: "CAPA", source: "Audit",
      linkedAuditId: selected.id, linkedAuditType: selected.auditType,
      findingRef: `${section.name} · ${cp.name}`,
      title: `${cp.name} — failed`,
      description: cp.notes ? `${cp.name}: ${cp.notes}` : cp.name,
      category: `${section.name}${selected.location ? " · " + selected.location : ""}`,
      severity: sevFromPriority(cp.priority),
      owner: cp.responsiblePerson || "", dueDate: cp.targetDate || "",
      correctiveAction: cp.correctiveAction || "",
      remarks: `Audit: ${selected.auditType} · ${selected.bakeryName} · Auditor ${selected.auditorName} · ${fmtDateTime(selected.dateTime)}`,
    });
    setCapaOpen(true);
  };

  const types = useMemo(() => ["All", ...Array.from(new Set(audits.map((a) => a.auditType).filter(Boolean)))], [audits]);

  const filtered = useMemo(() => audits
    .filter((a) => !preset || (a.parameters || []).some((p) => preset.includes(String(p.key || p.shortName || "").toLowerCase())))
    .filter((a) => type === "All" || a.auditType === type)
    .filter((a) => !q || `${a.bakeryName} ${a.auditorName} ${a.location} ${a.auditType}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => new Date(b.dateTime || 0) - new Date(a.dateTime || 0)), [audits, type, q, preset]);

  const params = selected?.parameters || [];
  const visibleParams = preset ? params.filter((p) => preset.includes(String(p.key || p.shortName || "").toLowerCase())) : params;
  const shownParams = visibleParams.length ? visibleParams : params;

  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle="Review completed and in-progress bakery audits with their full checklist responses.">
        {preset && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-800">Focused on: {preset.join(" / ")}</span>}
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {types.map((t) => (
            <Button key={t} size="sm" variant={type === t ? "default" : "outline"} data-testid={`audit-type-${t.toLowerCase()}`}
              className={type === t ? "h-9 bg-stone-800 hover:bg-stone-900" : "h-9"} onClick={() => setType(t)}>{t}</Button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search unit / auditor…" className="h-9 pl-8 text-sm" />
        </div>
      </div>

      {isLoading ? <LoadingState label="Loading audits…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? <EmptyState title="No audits" message={preset ? "No audits contain this checklist section yet." : "Audits recorded on mobile or web will appear here."} /> : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-2.5">Unit / Location</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Auditor</th>
                  <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-right">Score</th>
                  <th className="px-4 py-2.5 text-right">Critical</th><th className="px-4 py-2.5 text-right">Failed</th><th className="px-4 py-2.5">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((a) => (
                  <tr key={a.id} data-testid={`audit-row-${a.id}`} className="cursor-pointer hover:bg-amber-50/40" onClick={() => setSelected(a)}>
                    <td className="px-4 py-2.5"><span className="flex items-center gap-2 text-stone-800"><ClipboardCheck className="h-3.5 w-3.5 text-amber-700" />{a.bakeryName}{a.location ? ` · ${a.location}` : ""}</span></td>
                    <td className="px-4 py-2.5">{a.auditType}</td>
                    <td className="px-4 py-2.5 text-stone-600">{a.auditorName}</td>
                    <td className="px-4 py-2.5"><StatusBadge value={a.status} /></td>
                    <td className="px-4 py-2.5 text-right font-mono">{a.overallScore != null ? `${a.overallScore}%` : "—"}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${a.criticalCount ? "text-rose-600" : "text-stone-400"}`}>{a.criticalCount ?? 0}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${a.failedCount ? "text-rose-600" : "text-stone-400"}`}>{a.failedCount ?? 0}</td>
                    <td className="px-4 py-2.5 text-xs text-stone-500">{fmtDateTime(a.dateTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-2xl">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 font-display">{selected.auditType} Audit <StatusBadge value={selected.status} /></SheetTitle>
                <SheetDescription className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span>Unit: <strong>{selected.bakeryName}</strong></span>
                  <span>Auditor: <strong>{selected.auditorName}</strong></span>
                  <span>Score: <strong>{selected.overallScore}%</strong></span>
                  <span>{fmtDateTime(selected.dateTime)}</span>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-4">
                {shownParams.map((p) => (
                  <div key={p.id} data-testid={`audit-section-${p.key || p.index}`} className="rounded-lg border border-stone-200">
                    <div className="flex items-center justify-between border-b border-stone-200 bg-stone-50 px-4 py-2">
                      <span className="flex items-center gap-2 text-sm font-semibold text-stone-800">{p.name}{p.critical && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">CRITICAL</span>}</span>
                    </div>
                    <div className="divide-y divide-stone-100">
                      {(p.checkpoints || []).map((c) => {
                        const t = cpTone(c.status); const Icon = t.icon;
                        const failed = String(c.status || "").toLowerCase() === "fail";
                        return (
                          <div key={c.id} className="px-4 py-2">
                            <div className="flex items-start justify-between gap-3">
                              <span className="text-sm text-stone-700">{c.name}</span>
                              <span className={`flex shrink-0 items-center gap-1 text-xs font-medium ${t.cls}`}><Icon className="h-3.5 w-3.5" />{t.label}</span>
                            </div>
                            {c.notes && <div className="mt-0.5 text-xs text-stone-500">{c.notes}</div>}
                            {failed && (c.correctiveAction || c.responsiblePerson || c.targetDate) && (
                              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 rounded bg-rose-50 px-2 py-1 text-[11px] text-rose-700">
                                <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {c.correctiveAction || "Corrective action pending"}</span>
                                {c.responsiblePerson && <span>Owner: {c.responsiblePerson}</span>}
                                {c.targetDate && <span>Due: {c.targetDate}</span>}
                              </div>
                            )}
                            {failed && (
                              <div className="mt-1.5">
                                <Button data-testid={`audit-raise-capa-${c.id}`} size="sm" variant="outline" className="h-7 gap-1.5 border-rose-200 text-xs text-rose-700 hover:bg-rose-50" onClick={() => raiseCapa(p, c)}>
                                  <ClipboardPlus className="h-3.5 w-3.5" /> Create CAPA
                                </Button>
                              </div>
                            )}
                            {Array.isArray(c.photos) && c.photos.length > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-[11px] text-stone-400"><ImageIcon className="h-3 w-3" /> {c.photos.length} photo(s)</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CapaCreateDialog open={capaOpen} onOpenChange={setCapaOpen} prefill={capaPrefill} onCreated={() => setCapaOpen(false)} />
    </div>
  );
}
